
import React, { useEffect, useState, useCallback } from 'react';
import { Input } from 'reactstrap';
import SimpleBar from 'simplebar-react';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import { usePocketBase } from '../../PocketBaseContext';

const SearchOption = () => {
    const pb = usePocketBase();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState({
        products: [],
        customers: [],
        invoices: [],
        services: []
    });
    const [showResults, setShowResults] = useState(false);
    const [loading, setLoading] = useState(false);

    const performSearch = async (query) => {
        if (!query?.trim() || !pb) {
            setSearchResults({
                products: [],
                customers: [],
                invoices: [],
                services: []
            });
            return;
        }

        setLoading(true);
        try {
            const [products, customers, invoices, services] = await Promise.all([
                pb.collection('products').getList(1, 5, {
                    filter: `product_name ~ "${query}" || product_code ~ "${query}"`,
                }),
                pb.collection('customers').getList(1, 5, {
                    filter: `name ~ "${query}" || tax_number ~ "${query}"`,
                }),
                pb.collection('invoices').getList(1, 5, {
                    filter: `title ~ "${query}" || invoice_number ~ "${query}"`,
                }),
                pb.collection('services').getList(1, 5, {
                    filter: `name ~ "${query}"`,
                })
            ]);

            setSearchResults({
                products: products?.items || [],
                customers: customers?.items || [],
                invoices: invoices?.items || [],
                services: services?.items || []
            });
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults({
                products: [],
                customers: [],
                invoices: [],
                services: []
            });
        } finally {
            setLoading(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce((query) => performSearch(query), 300),
        [pb]  // pb'yi dependency array'e ekledik
    );

    useEffect(() => {
        if (searchQuery) {
            debouncedSearch(searchQuery);
            setShowResults(true);
        } else {
            setShowResults(false);
        }
    }, [searchQuery, debouncedSearch]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.search-box')) {
                setShowResults(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    return (
        <div className="search-box dropdown">
            <form className="app-search d-none d-md-block">
                <div className="position-relative">
                    <Input
                        type="text"
                        className="form-control"
                        placeholder="Ara... (müşteriler, ürünler, teklifler)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={() => setShowResults(true)}
                    />
                    <span className="mdi mdi-magnify search-widget-icon"></span>
                    {searchQuery && (
                        <span
                            className="mdi mdi-close-circle search-widget-icon search-widget-icon-close"
                            onClick={() => {
                                setSearchQuery('');
                                setShowResults(false);
                            }}
                        ></span>
                    )}

                    {showResults && (
                        <div className="dropdown-menu dropdown-menu-lg show">
                            <SimpleBar style={{ height: "320px" }}>
                                {loading ? (
                                    <div className="p-3 text-center">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Aranıyor...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {searchResults.products.length > 0 && (
                                            <div className="p-2">
                                                <h6 className="text-uppercase fw-semibold text-muted">Ürünler</h6>
                                                {searchResults.products.map(product => (
                                                    <Link
                                                        key={product.id}
                                                        to={`/reform-products/detail/${product.id}`}
                                                        className="dropdown-item"
                                                        onClick={() => setShowResults(false)}
                                                    >
                                                        <i className="ri-store-2-line me-2"></i>
                                                        {product.product_name}
                                                        <small className="text-muted ms-2">{product.product_code}</small>
                                                    </Link>
                                                ))}
                                            </div>
                                        )}

                                        {searchResults.customers.length > 0 && (
                                            <div className="p-2">
                                                <h6 className="text-uppercase fw-semibold text-muted">Müşteriler</h6>
                                                {searchResults.customers.map(customer => (
                                                    <Link
                                                        key={customer.id}
                                                        to={`/reform-customers/detail/${customer.id}`}
                                                        className="dropdown-item"
                                                        onClick={() => setShowResults(false)}
                                                    >
                                                        <i className="ri-user-2-line me-2"></i>
                                                        {customer.name}
                                                        <small className="text-muted ms-2">{customer.tax_number}</small>
                                                    </Link>
                                                ))}
                                            </div>
                                        )}

                                        {searchResults.invoices.length > 0 && (
                                            <div className="p-2">
                                                <h6 className="text-uppercase fw-semibold text-muted">Teklifler</h6>
                                                {searchResults.invoices.map(invoice => (
                                                    <Link
                                                        key={invoice.id}
                                                        to={`/reform-invoices/detail/${invoice.id}`}
                                                        className="dropdown-item"
                                                        onClick={() => setShowResults(false)}
                                                    >
                                                        <i className="ri-file-list-3-line me-2"></i>
                                                        {invoice.title}
                                                        <small className="text-muted ms-2">{invoice.invoice_number}</small>
                                                    </Link>
                                                ))}
                                            </div>
                                        )}

                                        {searchResults.services.length > 0 && (
                                            <div className="p-2">
                                                <h6 className="text-uppercase fw-semibold text-muted">Hizmetler</h6>
                                                {searchResults.services.map(service => (
                                                    <Link
                                                        key={service.id}
                                                        to={`/reform-services/detail/${service.id}`}
                                                        className="dropdown-item"
                                                        onClick={() => setShowResults(false)}
                                                    >
                                                        <i className="ri-service-line me-2"></i>
                                                        {service.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}

                                        {Object.values(searchResults).every(arr => arr.length === 0) && (
                                            <div className="p-3 text-center">
                                                <p className="mb-0 text-muted">Sonuç bulunamadı</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </SimpleBar>
                        </div>
                    )}
                </div>
            </form>
        </div>
    );
};

export default SearchOption;